/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import AutosizeInput from 'react-input-autosize';
import { OuiScreenReaderOnly } from '../../accessibility';
import { OuiFormControlLayout } from '../../form/form_control_layout';
import { OuiIcon } from '../../icon';
import { OuiComboBoxPill } from './combo_box_pill';
import { htmlIdGenerator } from '../../../services';
export class OuiComboBoxInput extends Component {
  state = {
    hasFocus: false
  };
  updatePosition = () => {
    // Wait a beat for the DOM to update, since we depend on DOM elements' bounds.
    requestAnimationFrame(() => {
      this.props.updatePosition();
    });
  };
  onFocus = event => {
    this.props.onFocus(event);
    this.setState({
      hasFocus: true
    });
  };
  onBlur = event => {
    if (this.props.onBlur) {
      this.props.onBlur(event);
    }
    this.setState({
      hasFocus: false
    });
  };
  componentDidUpdate(prevProps) {
    const {
      searchValue
    } = prevProps;

    // We need to update the position of everything if the user enters enough input to change
    // the size of the input.
    if (searchValue !== this.props.searchValue) {
      this.updatePosition();
    }
  }
  inputOnChange = event => {
    const {
      onChange,
      searchValue
    } = this.props;
    if (onChange) {
      onChange(event.target.value);
    }
  };
  inputRefCallback = ref => {
    const {
      autoSizeInputRef
    } = this.props;
    if (autoSizeInputRef) {
      autoSizeInputRef(ref);
    }
  };
  render() {
    const {
      compressed,
      focusedOptionId,
      fullWidth,
      hasSelectedOptions,
      icon: iconProp,
      id,
      inputRef,
      isDisabled,
      isListOpen,
      noIcon,
      onClear,
      onClick,
      onCloseListClick,
      onOpenListClick,
      onRemoveOption,
      placeholder,
      rootId,
      searchValue,
      selectedOptions,
      singleSelection: singleSelectionProp,
      toggleButtonRef,
      value,
      prepend,
      append,
      isLoading,
      autoFocus
    } = this.props;
    const singleSelection = Boolean(singleSelectionProp);
    const asPlainText = singleSelectionProp && typeof singleSelectionProp === 'object' && singleSelectionProp.asPlainText || false;
    const pills = selectedOptions ? selectedOptions.map(option => {
      const {
        key,
        label,
        color,
        onClick,
        ...rest
      } = option;
      const pillOnClose = isDisabled || singleSelection || onClick ? undefined : onRemoveOption;
      return <OuiComboBoxPill option={option} onClose={pillOnClose} key={key ?? label.toLowerCase()} color={color} onClick={onClick} onClickAriaLabel={onClick ? 'Change' : undefined} asPlainText={asPlainText} {...rest}>
              {label}
            </OuiComboBoxPill>;
    }) : null;
    let removeOptionMessage;
    let removeOptionMessageId;
    if (this.state.hasFocus) {
      const readPlaceholder = placeholder ? `${placeholder}.` : '';
      const removeOptionMessageContent = `Combo box. Selected. ${searchValue ? `${searchValue}. Selected. ` : ''}${selectedOptions && selectedOptions.length > 0 ? `${value}. Press Backspace to delete ${selectedOptions[selectedOptions.length - 1].label}. ` : ''}Combo box input. ${readPlaceholder} Type some text or, to display a list of choices, press Down Arrow. ` + 'To exit the list of choices, press Escape.';
      removeOptionMessageId = htmlIdGenerator()();

      // aria-live="assertive" will read this message aloud immediately once it enters the DOM.
      // We'll render to the DOM when the input gains focus and remove it when the input loses focus.
      // We'll use aria-hidden to prevent default aria information from being read by the screen
      // reader.
      removeOptionMessage = <OuiScreenReaderOnly>
          <span aria-live="assertive" id={removeOptionMessageId}>
            {removeOptionMessageContent}
          </span>
        </OuiScreenReaderOnly>;
    }
    let placeholderMessage;
    if (placeholder && selectedOptions && !selectedOptions.length && !searchValue) {
      placeholderMessage = <p className="ouiComboBoxPlaceholder">{placeholder}</p>;
    }
    const clickProps = {};
    if (!isDisabled && onClear && hasSelectedOptions) {
      clickProps.clear = {
        'data-test-subj': 'comboBoxClearButton',
        onClick: onClear
      };
    }
    let formControlIcon;
    if (!noIcon) {
      formControlIcon = {
        'aria-label': isListOpen ? 'Close list of options' : 'Open list of options',
        'data-test-subj': 'comboBoxToggleListButton',
        disabled: isDisabled,
        onClick: isListOpen && !isDisabled ? onCloseListClick : onOpenListClick,
        ref: toggleButtonRef,
        side: 'right',
        type: 'arrowDown'
      };
    }
    let icon;
    if (!!iconProp) {
      const iconClasses = classNames('ouiComboBoxPill', 'ouiComboBoxIcon');
      icon = <OuiIcon type={typeof iconProp === 'string' ? iconProp : 'search'} size={compressed ? 's' : 'm'} className={iconClasses} data-test-subj="comboBoxIcon" />;
    }
    const wrapClasses = classNames('ouiComboBox__inputWrap', {
      'ouiComboBox__inputWrap--compressed': compressed,
      'ouiComboBox__inputWrap--fullWidth': fullWidth,
      'ouiComboBox__inputWrap--noWrap': singleSelection,
      'ouiComboBox__inputWrap-isLoading': isLoading,
      'ouiComboBox__inputWrap-isClearable': onClear,
      'ouiComboBox__inputWrap--inGroup': prepend || append
    });
    return <OuiFormControlLayout icon={formControlIcon} {...clickProps} isLoading={isLoading} compressed={compressed} fullWidth={fullWidth} prepend={prepend} append={append}>
        <div className={wrapClasses} data-test-subj="comboBoxInput" onClick={onClick} tabIndex={-1} // becomes onBlur event's relatedTarget, otherwise relatedTarget is null when clicking on this div
      >
          {icon}
          {!singleSelection || !searchValue ? pills : null}
          {placeholderMessage}
          <AutosizeInput aria-activedescendant={focusedOptionId} aria-controls={isListOpen ? rootId('listbox') : ''} className="ouiComboBox__input" data-test-subj="comboBoxSearchInput" disabled={isDisabled} id={id} inputRef={inputRef} onBlur={this.onBlur} onChange={this.inputOnChange} onFocus={this.onFocus} ref={this.inputRefCallback} role="textbox" style={{
          fontSize: 14
        }} value={searchValue} autoFocus={autoFocus} />
          {removeOptionMessage}
        </div>
      </OuiFormControlLayout>;
  }
}
OuiComboBoxInput.propTypes = {
  autoSizeInputRef: PropTypes.any,
  compressed: PropTypes.bool.isRequired,
  focusedOptionId: PropTypes.string,
  fullWidth: PropTypes.bool,
  hasSelectedOptions: PropTypes.bool.isRequired,
  icon: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addBookmark", "addDataApp", "advancedSettingsApp", "aggregate", "alert", "analyzeEvent", "annotation", "anomalyDetection", "apmApp", "apmTrace", "apps", "appSearchApp", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "bookOpen", "boxesHorizontal", "boxesVertical", "branch", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "codeApp", "chatLeft", "chatRight", "check", "checkInCircleEmpty", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "color", "compass", "compute", "console", "consoleApp", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "crossInACircleFilled", "crossInCircleEmpty", "crossInCircleFilled", "currency", "cut", "dashboard", "dashboardApp", "database", "dataVisualizer", "devToolsApp", "discoverApp", "document", "documentation", "documentEdit", "documents", "dot", "download", "dockedBottom", "dockedDetached", "dockedLeft", "dockedRight", "dockedTakeover", "dockedTop", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignLeft", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "functionAdd", "gear", "generate", "generateContent", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "history", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "inputOutput", "inspect", "integrationGeneral", "integrationObservability", "integrationSearch", "integrationSecurity", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "layers", "lensApp", "lineChart", "link", "list", "listAdd", "lock", "lockOpen", "logsApp", "logoAerospike", "logoApache", "logoAppSearch", "logoAWS", "logoAWSMono", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticsearch", "logoElasticStack", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOpenSearch", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "partial", "paperClip", "pause", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "polygon", "popout", "power", "pulse", "push", "questionInCircle", "quote", "radar", "radius", "recent", "recentlyViewedApp", "redeploy", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "rocket", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "shard", "share", "snowflake", "sortable", "sortDown", "sortLeft", "sortRight", "sortUp", "spacesApp", "sparkleFilled", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "swatchInput", "symlink", "tableOfContents", "tableDensityExpanded", "tableDensityCompact", "tableDensityNormal", "tag", "tear", "temperature", "thumbsDown", "thumbsUp", "timeline", "timelionApp", "timeslider", "training", "trash", "upgradeAssistantApp", "uptimeApp", "undeploy", "unfold", "unlink", "user", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visBuilder", "visBuilderSavedObject", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visQueryDQL", "visQueryPPL", "visQueryPromQL", "visQuerySQL", "visTable", "visTagCloud", "visText", "visTimelion", "visualizeApp", "visVega", "visVisualBuilder", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram", "wsAnalytics", "wsEssentials", "wsObservability", "wsSearch", "wsSecurityAnalytics", "wsSelector", "navAdministration", "navAiFlow", "navAlerting", "navAnomalyDetection", "navDashboards", "navData", "navDetectionRules", "navDevtools", "navDiscover", "navExperiments", "navGetStarted", "navInfo", "navInfra", "navIntegrations", "navJudgements", "navManage", "navMaps", "navModels", "navNotebooks", "navNotifications", "navOverview", "navQuerySets", "navReports", "navSecurityCases", "navSecurityFindings", "navServiceMap", "navServices", "navSlos", "navThreatIntel", "navTicketing", "navUi", "navSearchConfigurationsln"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]).isRequired, PropTypes.bool.isRequired]),
  id: PropTypes.string,
  inputRef: PropTypes.any,
  isDisabled: PropTypes.bool,
  isListOpen: PropTypes.bool.isRequired,
  noIcon: PropTypes.bool.isRequired,
  onBlur: PropTypes.any,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  onClick: PropTypes.func,
  onCloseListClick: PropTypes.func.isRequired,
  onFocus: PropTypes.any.isRequired,
  onOpenListClick: PropTypes.func.isRequired,
  onRemoveOption: PropTypes.func,
  placeholder: PropTypes.string,
  rootId: PropTypes.any.isRequired,
  searchValue: PropTypes.string.isRequired,
  selectedOptions: PropTypes.arrayOf(PropTypes.shape({
    isGroupLabelOption: PropTypes.bool,
    label: PropTypes.string.isRequired,
    key: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      isGroupLabelOption: PropTypes.bool,
      label: PropTypes.string.isRequired,
      key: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.any.isRequired),
      value: PropTypes.any,
      className: PropTypes.string,
      "aria-label": PropTypes.string,
      "data-test-subj": PropTypes.string
    }).isRequired),
    value: PropTypes.any,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string
  }).isRequired),
  singleSelection: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.shape({
    asPlainText: PropTypes.bool
  }).isRequired]),
  toggleButtonRef: PropTypes.any,
  updatePosition: PropTypes.func.isRequired,
  value: PropTypes.string,
  prepend: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired).isRequired]),
  append: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired).isRequired]),
  isLoading: PropTypes.bool,
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};
try {
  OuiComboBoxInput.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3077135706/src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
    description: '',
    displayName: 'OuiComboBoxInput',
    methods: [],
    props: {
      autoSizeInputRef: {
        defaultValue: null,
        description: '',
        name: 'autoSizeInputRef',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: '(instance: AutosizeInput & HTMLInputElement) => void'
        }
      },
      compressed: {
        defaultValue: null,
        description: '',
        name: 'compressed',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      },
      focusedOptionId: {
        defaultValue: null,
        description: '',
        name: 'focusedOptionId',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      fullWidth: {
        defaultValue: null,
        description: '',
        name: 'fullWidth',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      hasSelectedOptions: {
        defaultValue: null,
        description: '',
        name: 'hasSelectedOptions',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      },
      icon: {
        defaultValue: null,
        description: '',
        name: 'icon',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'boolean | IconType'
        }
      },
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      inputRef: {
        defaultValue: null,
        description: '',
        name: 'inputRef',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: '(instance: HTMLInputElement) => void'
        }
      },
      isDisabled: {
        defaultValue: null,
        description: '',
        name: 'isDisabled',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isListOpen: {
        defaultValue: null,
        description: '',
        name: 'isListOpen',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      },
      noIcon: {
        defaultValue: null,
        description: '',
        name: 'noIcon',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      },
      onBlur: {
        defaultValue: null,
        description: '',
        name: 'onBlur',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'FocusEventHandler<HTMLInputElement>'
        }
      },
      onChange: {
        defaultValue: null,
        description: '',
        name: 'onChange',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: '(searchValue: string) => void'
        }
      },
      onClear: {
        defaultValue: null,
        description: '',
        name: 'onClear',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: '() => void'
        }
      },
      onClick: {
        defaultValue: null,
        description: '',
        name: 'onClick',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: '() => void'
        }
      },
      onCloseListClick: {
        defaultValue: null,
        description: '',
        name: 'onCloseListClick',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: '() => void'
        }
      },
      onFocus: {
        defaultValue: null,
        description: '',
        name: 'onFocus',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: 'FocusEventHandler<HTMLInputElement>'
        }
      },
      onOpenListClick: {
        defaultValue: null,
        description: '',
        name: 'onOpenListClick',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: '() => void'
        }
      },
      onRemoveOption: {
        defaultValue: null,
        description: '',
        name: 'onRemoveOption',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'OptionHandler<T>'
        }
      },
      placeholder: {
        defaultValue: null,
        description: '',
        name: 'placeholder',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      rootId: {
        defaultValue: null,
        description: '',
        name: 'rootId',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: '(idSuffix?: string) => string'
        }
      },
      searchValue: {
        defaultValue: null,
        description: '',
        name: 'searchValue',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      selectedOptions: {
        defaultValue: null,
        description: '',
        name: 'selectedOptions',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'OuiComboBoxOptionOption<T>[]'
        }
      },
      singleSelection: {
        defaultValue: null,
        description: '',
        name: 'singleSelection',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'boolean | OuiComboBoxSingleSelectionShape'
        }
      },
      toggleButtonRef: {
        defaultValue: null,
        description: '',
        name: 'toggleButtonRef',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: '(instance: HTMLSpanElement | HTMLButtonElement) => void'
        }
      },
      updatePosition: {
        defaultValue: null,
        description: '',
        name: 'updatePosition',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: 'UpdatePositionHandler'
        }
      },
      value: {
        defaultValue: null,
        description: '',
        name: 'value',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      prepend: {
        defaultValue: null,
        description: '',
        name: 'prepend',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'PrependAppendType'
        }
      },
      append: {
        defaultValue: null,
        description: '',
        name: 'append',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'PrependAppendType'
        }
      },
      isLoading: {
        defaultValue: null,
        description: '',
        name: 'isLoading',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      autoFocus: {
        defaultValue: null,
        description: '',
        name: 'autoFocus',
        parent: {
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'src/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'OuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['OuiComboBoxInputProps', 'CommonProps']
  };
} catch (e) {}